/**
 * VireoHR - Owner Locations Screen (VIEW ONLY)
 * Ultra-Minimal Design
 * Only Super Admin can manage locations
 */

import { collection, query, where, getDocs } from 'firebase/firestore';
import React, { useState, useEffect } from 'react';

import { Badge } from '@/components/Badge';
import { Card } from '@/components/Card';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { firestore } from '@/lib/firebase/client';

interface Location {
  id: string;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  geofenceRadius: number;
  maxEmployees?: number;
  active: boolean;
}

export default function Locations() {
  const { user } = useAuth();
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.tenantId) {
      fetchLocations();
    }
  }, [user]);

  const fetchLocations = async () => {
    if (!user?.tenantId) return;

    try {
      const branchesRef = collection(firestore, 'branches');
      const q = query(branchesRef, where('tenantId', '==', user.tenantId));
      const snapshot = await getDocs(q);
      
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Location[];

      setLocations(data);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout title="Locations">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#0D9488] border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Locations">
      <div className="p-4 space-y-4">
        <h1 className="text-base font-semibold text-gray-900">Your Locations</h1>

        {locations.length === 0 ? (
          <Card>
            <div className="text-center py-8">
              <p className="text-sm text-gray-500">No locations found</p>
            </div>
          </Card>
        ) : (
          <div className="space-y-3">
            {locations.map((location) => (
              <Card key={location.id}>
                <div className="space-y-3">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">{location.name}</h3>
                      <p className="text-xs text-gray-500 mt-0.5">{location.address}</p>
                    </div>
                    <Badge variant={location.active ? 'success' : 'default'}>
                      {location.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>

                  {/* Details */}
                  <div className="pt-3 border-t border-gray-100">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-gray-500">Geofence Radius</p>
                        <p className="text-sm font-medium text-gray-900 mt-0.5">{location.geofenceRadius}m</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Coordinates</p>
                        <p className="text-sm font-medium text-gray-900 mt-0.5">
                          {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}
                        </p>
                      </div>
                    </div>
                    {location.maxEmployees && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <p className="text-xs text-gray-500">Max Employees</p>
                        <p className="text-sm font-medium text-gray-900 mt-0.5">{location.maxEmployees}</p>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
